import GridItem, { IGridItemData } from '../components/List/GridItem';
import { IBlockProps, RadiusStyle } from './types';
import {
  ImageServiceType,
  parseImageUrl,
  useCoalescedSize,
} from '@omni/kit/utilities/utilities';
import MediaPersonalizationService, {
  IMediaPlays,
} from '@omni/kit/services/MediaPersonalizationService';
import React, { useCallback, useEffect, useState } from 'react';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { StyleSheet, View } from 'react-native';

import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import { EActionHandlers } from '@omni/kit/feeds/ActionHandlerTypes';
import Environment from '@omni/kit/Environment';
import { ILogoImage } from '@omni/kit/services/Types';
import KitIcon from '@omni/kit/components/KitIcon';
import KitList from '@omni/kit/components/KitList';
import KitListItem from '@omni/kit/components/KitListItem';
import KitText from '@omni/kit/components/KitText';
import KitTouchable from '@omni/kit/components/KitTouchable';
import { NATIVE_EVENT_KEY } from '@omni/kit/Types';
import { NativeTopBarStyle } from '@omni/kit/utilities/NativeHelpers';
import Show from '@omni/kit/components/Show';
import Spacing from '@omni/kit/theming/Spacing';
import { dispatch } from '@omni/kit/ActionHandler';
import { useAppContext } from '@omni/kit/contexts/AppContext';
import { useAuth } from '@omni/kit/contexts/AuthContext';
import { useScreenContext } from '@omni/kit/contexts/ScreenContext';
import { useScreenVisibility } from '@omni/kit/contexts/ScreenVisibilityContext';
import { useTranslation } from 'react-i18next';
import useUserInfo from '@omni/kit/hooks/useUserInfo';

const debug = require('debug')('tca:blocks:ContinuePlayingBlock');
const ASPECT_RATIO = 16 / 9;
const MAX_ITEM_WIDTH = 250;
const SILHOUETTE_IMAGE_SIZE = 76;
const ENDING_THRESHOLD = 1; // in seconds

export interface ContinuePlayingBlockProps extends IBlockProps {
  appKey?: string;
}

interface MediaPlayItem extends IGridItemData {
  averageColor?: string;
}

export default function ContinuePlayingBlock({
  appKey,
}: ContinuePlayingBlockProps): JSX.Element | null {
  const activeApp = useAppContext();
  const brand = activeApp?._embedded?.branding;
  const silhouetteLogoImage: ILogoImage | undefined =
    brand?._embedded?.['silhouette-logo-image'];

  const { t } = useTranslation(['continuePlaying']);
  const { sizeClass } = useSizeClass();
  const isScreenVisible = useScreenVisibility();
  const { accessToken, isAuthenticated } = useAuth();
  const { user, isLoading: isUserLoading } = useUserInfo(appKey, accessToken);
  const { edgeSpacing, interItemSpacing, viewPortWidth } = useScreenContext({
    fixedSpacingType: undefined,
  });

  const [refreshItems, setRefreshItems] = useState<boolean>(false);
  const [itemWidth, setItemWidth] = useState<number>(55);
  const [itemHeight, setItemHeight] = useState<number>(55);
  const [items, setItems] = useState<MediaPlayItem[]>([]);
  const {
    coalescedWidth: coalescedImageWidth,
    coalescedHeight: coalescedImageHeight,
  } = useCoalescedSize(itemWidth, itemHeight);

  const isLargeScreen = sizeClass !== SizeClass.Small;
  const containerPadding = isLargeScreen ? edgeSpacing + Spacing.l : Spacing.l;
  const itemsSize = items.length - 1;

  const renderPlayHistoryIcon = () => {
    return (
      <View style={styles.noPlayHistoryIcon}>
        <KitIcon color={Colors.N300} name={'media'} size={20} />
      </View>
    );
  };

  const renderNoPlayHistory = () => {
    return (
      <View
        style={{
          paddingHorizontal: containerPadding,
        }}
      >
        <KitListItem
          bottomBorder={false}
          ImageComponent={renderPlayHistoryIcon()}
          showRightIconOnAndroid={true}
          style={styles.noPlayHistory}
          title={t('emptyPlayHistoryMessage')}
          titleColor={Colors.N500}
          titleFontSize={16}
        />
      </View>
    );
  };

  const onListItemPressed = (item: MediaPlayItem, _index: number): void => {
    dispatch(item.action);
  };

  const listKeyExtractor = useCallback((item: MediaPlayItem, index: number) => {
    return index.toString();
  }, []);

  const renderItem = ({
    item,
    index,
  }: {
    item: MediaPlayItem;
    index: number;
  }) => {
    const paddingRight = index === itemsSize ? Spacing.l : 0;
    const paddingLeft = index === 0 && isLargeScreen ? 0 : Spacing.l;

    return (
      <GridItem
        averageColor={item.averageColor}
        data={item}
        fixedWidthEnabled={true}
        height={itemHeight}
        ignoreImageLoad={true}
        imageHeight={coalescedImageHeight}
        imageRadius={RadiusStyle.Medium}
        imageWidth={coalescedImageWidth}
        onPress={() => {
          onListItemPressed(item, index);
        }}
        silhouetteLogoImage={parseImageUrl(
          silhouetteLogoImage?._links?.dynamic?.href || '',
          SILHOUETTE_IMAGE_SIZE,
          SILHOUETTE_IMAGE_SIZE,
          ImageServiceType.WhitePng
        )}
        silhouetteLogoImageSize={SILHOUETTE_IMAGE_SIZE}
        shadowEnabled={true}
        style={{
          paddingLeft: paddingLeft,
          paddingRight: paddingRight,
        }}
        subtitleTextColor={Colors.N400}
        width={itemWidth}
      />
    );
  };

  const updateLayout = (): void => {
    const numItemsVisible = isLargeScreen ? 2.75 : 1.75;
    const width = Math.min(viewPortWidth / numItemsVisible, MAX_ITEM_WIDTH);
    const height = width / ASPECT_RATIO;

    setItemWidth(width);
    setItemHeight(height);
  };

  const fetchMediaPlays = async (context: string) => {
    try {
      if (!user || !appKey) return;

      const response = await MediaPersonalizationService.getAll({
        appKey,
        filters: [
          ['end-user.id', user.userID],
          ['app_key', appKey],
        ],
        pages: [
          ['number', 0],
          ['size', 10],
        ],
        sorts: ['-updated_at'],
        includes: ['media-item'],
        getFromCache: context === 'cache',
      });

      if (response?.body && response?.body?.count > 0) {
        const mediaPlays = response.body?._embedded['media-plays'];
        const items = mediaPlays.map((mediaPlay: any) =>
          parseMediaPlay(mediaPlay as IMediaPlays)
        );

        setItems(items);
      }
    } catch (e) {
      debug('Failed to fetch media plays', e);
    }
  };

  const initialize = async () => {
    await fetchMediaPlays('cache');
    await fetchMediaPlays('network');
  };

  useEffect(() => {
    if (isAuthenticated && user && !isUserLoading) {
      initialize();
    }
  }, [isAuthenticated, user, isUserLoading]);

  useEffect(() => {
    if (refreshItems) {
      setRefreshItems(false);
      initialize();
    }
  }, [refreshItems]);

  useEffect(() => {
    if (viewPortWidth > 0) {
      updateLayout();
    }
  }, [viewPortWidth, interItemSpacing, edgeSpacing]);

  useEffect(() => {
    setRefreshItems(isScreenVisible);
  }, [isScreenVisible]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <View style={styles.container}>
      <KitTouchable
        style={[
          styles.titleWrapper,
          {
            paddingHorizontal: containerPadding,
          },
        ]}
        borderRadius={0}
        ripple={false}
        onPress={() => {
          if (items.length === 0) return;

          dispatch({
            handler: 'continuePlaying',
            nativeEventKey: NATIVE_EVENT_KEY.CONTINUE_PLAYING,
          });
        }}
        underlayColor={Colors.N0}
      >
        <View style={styles.titleContainer}>
          <KitText h2 bold={true} color={Colors.N900}>
            {t('headerTitle')}
          </KitText>
          <Show show={items.length > 0}>
            <KitIcon
              color={Colors.N300}
              name={'arrow-right'}
              size={20}
              style={{ marginLeft: Spacing.s }}
            />
          </Show>
        </View>
      </KitTouchable>
      {items.length > 0 ? (
        <View
          style={{
            paddingLeft: isLargeScreen ? edgeSpacing + Spacing.l : 0,
          }}
        >
          <KitList<MediaPlayItem>
            data={items}
            horizontal={true}
            initialNumToRender={10}
            keyExtractor={listKeyExtractor}
            maxToRenderPerBatch={10}
            renderItem={renderItem}
            scrollEnabled={true}
            showsHorizontalScrollIndicator={false}
          />
        </View>
      ) : (
        renderNoPlayHistory()
      )}
    </View>
  );
}

const parseMediaPlay = (mediaPlay: IMediaPlays): MediaPlayItem => {
  const host = Environment.feedsService;
  const mediaItem = mediaPlay._embedded['media-item'];
  const images = mediaItem._embedded?.images || [];
  const image = images[0]?._links?.related?.href;
  let durationLeftMs = mediaPlay.duration_ms;
  let percentage = 0;

  if (mediaPlay.progress_ms) {
    durationLeftMs = mediaPlay.duration_ms - mediaPlay.progress_ms;
    percentage = (mediaPlay.progress_ms * 100) / mediaPlay.duration_ms;
  }

  return {
    averageColor: images[0]?.average_color_hex,
    title: mediaItem.title,
    subtitle: prepareDurationLeft(durationLeftMs),
    image,
    percentage,
    action: {
      handler: EActionHandlers.BlockPage,
      url: `${host}/media-detail/${mediaItem.id}`,
      topBarStyle: NativeTopBarStyle.Transparent,
      nativeEventKey: `${NATIVE_EVENT_KEY.MEDIA_DETAIL}-${mediaItem.id}`,
    },
  };
};

const prepareDurationLeft = (duration: number): string => {
  const durationLeft = [];

  duration = Math.floor(duration / 1000);
  const seconds = duration % 60;

  duration = Math.floor(duration / 60);
  const minutes = duration % 60;

  duration = Math.floor(duration / 60);
  const hours = duration;

  if (hours || minutes) {
    if (hours) {
      durationLeft.push(`${hours} hr`);
    }

    if (minutes) {
      durationLeft.push(`${minutes} min`);
    }
  } else {
    durationLeft.push(`${seconds > ENDING_THRESHOLD ? seconds : 0} sec`);
  }

  return `${durationLeft.join(' ')} left`;
};

const styles = StyleSheet.create({
  container: {
    marginTop: Spacing.xxl,
  },
  titleWrapper: {
    marginBottom: Spacing.m,
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  noPlayHistory: {
    borderColor: Colors.N100,
    borderRadius: BorderRadius.l,
    borderWidth: 1,
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
  },
  noPlayHistoryIcon: {
    alignItems: 'center',
    backgroundColor: Colors.N50,
    borderRadius: BorderRadius.m,
    display: 'flex',
    justifyContent: 'center',
    padding: Spacing.s,
  },
});
